/**
 * APEX v7.7.1+ — Game-level Insights
 *
 * Analyse fine du déroulé d'une game : morts contextualisées, timing objectifs,
 * itemization, vision, spike niveau, draft.
 *
 * Voir docs/PRD_OBJECTIVES_V7.7_FINAL.md section 13.
 *
 * Entrypoint qui agrège les détections par catégorie.
 * Les patterns sont organisés en 6 catégories :
 *   - A : morts contextualisées (v7.7.1 — premiers patterns livrés)
 *   - B : timing objectifs (v7.7.2)
 *   - C : itemization (v7.7.3)
 *   - D : vision (v7.7.3)
 *   - E : spike niveau (v7.7.3)
 *   - F : draft/matchups (v7.7.3)
 */

import { analyzeDeaths } from './deaths';
import { analyzeObjectives } from './objectives';
import { analyzeItems, analyzeVision, analyzeLevels, analyzeDraft } from './itemsVisionLevelsDraft';
import { analyzePositives } from './positives';

/**
 * Retourne tous les insights détectés sur une game.
 *
 * @param {Object} game - la game à analyser
 * @param {Object} context - contexte additionnel
 *   - userStats : stats agrégées de l'user (pour comparaisons à TA baseline)
 *   - userSettings : settings (current_tier, etc.)
 *   - allGames : pour calculer baselines comme "ton WR habituel sur ce matchup"
 * @returns {Array} liste d'insights triés par importance
 */
export function analyzeGame(game, context = {}) {
  if (!game) return [];

  // Game annulée : pas d'analyse
  if (isCancelled(game)) return [];

  const insights = [];

  // ─────────────────────────────────────────────────
  // Catégorie A : morts contextualisées (v7.7.1 + A1/A2/A4 v7.7.2)
  // ─────────────────────────────────────────────────
  insights.push(...analyzeDeaths(game, context));

  // ─────────────────────────────────────────────────
  // Catégorie B : timing objectifs (v7.7.2)
  // ─────────────────────────────────────────────────
  insights.push(...analyzeObjectives(game, context));

  // ─────────────────────────────────────────────────
  // Catégories C/D/E/F (v7.7.3)
  //   C : itemization · D : vision · E : spike niveau · F : draft
  // ─────────────────────────────────────────────────
  insights.push(...analyzeItems(game, context));
  insights.push(...analyzeVision(game, context));
  insights.push(...analyzeLevels(game, context));
  insights.push(...analyzeDraft(game, context));
  insights.push(...analyzePositives(game, context));

  // Tri par severity (critical > warning > info > positive)
  return insights.sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity));
}

function severityOrder(sev) {
  switch (sev) {
    case 'critical': return 0;
    case 'warning': return 1;
    case 'info': return 2;
    case 'positive': return 3;
    default: return 99;
  }
}

function isCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}
